/**
 * DealerLegalDocs — Händlereigene Rechtstexte (Impressum/Datenschutz/AGB/Widerruf):
 * pro Dokument wahlweise Homepage-URL (Auto-Update) ODER Datei-Upload (Fallback).
 * Zeigt systemweite Link-Erreichbarkeit (grün/rot) und speichert eigenständig.
 */
import { useEffect, useState, useRef } from "react";
import { Shield, Link2, Upload, CheckCircle2, AlertTriangle, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

const DOCS = [
    ["impressum", "Impressum"],
    ["datenschutz", "Datenschutzerklärung"],
    ["agb", "AGB"],
    ["widerruf", "Widerrufsbelehrung"],
];

export default function DealerLegalDocs({ currentUser, onSaved }) {
    const [urls, setUrls] = useState({});
    const [uploads, setUploads] = useState({});
    const [health, setHealth] = useState({});
    const [busy, setBusy] = useState("");
    const fileRefs = useRef({});

    useEffect(() => {
        const u = {}, up = {};
        DOCS.forEach(([k]) => { u[k] = currentUser[`legal_${k}_url`] || ""; up[k] = currentUser[`legal_${k}_upload_url`] || ""; });
        setUrls(u); setUploads(up);
        loadHealth();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadHealth = async () => {
        try { const { data } = await api.get("/users/me/legal-docs/health"); setHealth(data.links || {}); } catch { /* ignore */ }
    };

    const saveUrl = async (kind) => {
        setBusy(`url-${kind}`);
        try {
            await api.patch("/users/me/dealer-profile", { [`legal_${kind}_url`]: urls[kind]?.trim() || null });
            toast.success("Link gespeichert.");
            await loadHealth(); onSaved?.();
        } catch (e) { toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen"); }
        finally { setBusy(""); }
    };

    const doUpload = async (kind, file) => {
        if (!file) return;
        setBusy(`up-${kind}`);
        try {
            const fd = new FormData(); fd.append("file", file);
            const { data } = await api.post(`/users/me/legal-docs/${kind}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
            setUploads((p) => ({ ...p, [kind]: data.url }));
            toast.success("Datei hochgeladen.");
            await loadHealth(); onSaved?.();
        } catch (e) { toast.error(e.response?.data?.detail || "Upload fehlgeschlagen"); }
        finally { setBusy(""); }
    };

    const removeUpload = async (kind) => {
        setBusy(`up-${kind}`);
        try {
            await api.delete(`/users/me/legal-docs/${kind}/upload`);
            setUploads((p) => ({ ...p, [kind]: "" }));
            toast.success("Datei entfernt.");
            await loadHealth(); onSaved?.();
        } catch (e) { toast.error(e.response?.data?.detail || "Entfernen fehlgeschlagen"); }
        finally { setBusy(""); }
    };

    const Badge = ({ kind }) => {
        const h = health[kind];
        if (!h || h.ok === null || !h.url) return <span className="text-[11px] text-gray-400">nicht gesetzt</span>;
        if (h.ok) return <span className="inline-flex items-center gap-1 text-[11px] font-bold text-emerald-600" data-testid={`legal-health-ok-${kind}`}><CheckCircle2 className="w-3.5 h-3.5" /> erreichbar</span>;
        return <span className="inline-flex items-center gap-1 text-[11px] font-bold text-red-600" data-testid={`legal-health-dead-${kind}`}><AlertTriangle className="w-3.5 h-3.5" /> nicht erreichbar{h.status ? ` (${h.status})` : ""}</span>;
    };

    return (
        <div className="mt-6 pt-6 border-t border-gray-100" data-testid="dealer-legal-section">
            <div className="font-bold text-[#111827] flex items-center gap-2">
                <Shield className="w-4 h-4 text-[#111827]" /> Eigene Rechtstexte (Impressum, Datenschutz, AGB, Widerruf)
            </div>
            <div className="text-xs text-gray-600 mt-1 mb-4">
                Verknüpfen Sie Ihre Homepage-Seiten (Änderungen werden automatisch übernommen) oder laden Sie eine Datei hoch, falls Sie keine Homepage haben. Diese Texte werden auf Ihren öffentlichen Kundenseiten (z. B. Leasingrückgabe) angezeigt. Verantwortlich i. S. d. DSGVO bleiben Sie.
            </div>

            <div className="space-y-4">
                {DOCS.map(([kind, label]) => (
                    <div key={kind} className="rounded-lg border border-gray-200 p-3" data-testid={`legal-doc-${kind}`}>
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-bold text-gray-800">{label}</span>
                            <Badge kind={kind} />
                        </div>
                        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                            <div className="flex-1 flex items-center gap-2">
                                <Link2 className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                <input
                                    type="url" placeholder="https://ihre-homepage.de/datenschutz"
                                    value={urls[kind] || ""}
                                    onChange={(e) => setUrls((p) => ({ ...p, [kind]: e.target.value }))}
                                    className="block w-full rounded-sm border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-[#FFCC00] outline-none"
                                    data-testid={`legal-url-input-${kind}`}
                                />
                                <button onClick={() => saveUrl(kind)} disabled={busy === `url-${kind}`} className="rounded-sm bg-gray-900 px-3 py-2 text-xs font-bold text-white hover:bg-black disabled:opacity-60" data-testid={`legal-url-save-${kind}`}>
                                    {busy === `url-${kind}` ? <Loader2 className="w-4 h-4 animate-spin" /> : "Speichern"}
                                </button>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="text-[11px] text-gray-400">oder</span>
                                <input ref={(el) => (fileRefs.current[kind] = el)} type="file" accept="application/pdf,.pdf,.html,.htm" className="hidden" onChange={(e) => doUpload(kind, e.target.files?.[0])} data-testid={`legal-file-input-${kind}`} />
                                <button onClick={() => fileRefs.current[kind]?.click()} disabled={busy === `up-${kind}`} className="inline-flex items-center gap-1.5 rounded-sm border border-gray-300 px-3 py-2 text-xs font-bold text-gray-700 hover:bg-gray-50 disabled:opacity-60" data-testid={`legal-upload-btn-${kind}`}>
                                    {busy === `up-${kind}` ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Datei
                                </button>
                            </div>
                        </div>
                        {uploads[kind] && (
                            <div className="mt-2 flex items-center gap-2 text-[11px] text-emerald-700" data-testid={`legal-upload-active-${kind}`}>
                                <CheckCircle2 className="w-3.5 h-3.5" /> Hochgeladene Datei aktiv (hat Vorrang)
                                <a href={uploads[kind]} target="_blank" rel="noreferrer" className="underline">ansehen</a>
                                <button onClick={() => removeUpload(kind)} className="ml-1 inline-flex items-center gap-1 text-red-600 hover:underline" data-testid={`legal-upload-remove-${kind}`}><Trash2 className="w-3.5 h-3.5" /> entfernen</button>
                            </div>
                        )}
                    </div>
                ))}
            </div>

            <RetentionSetting currentUser={currentUser} onSaved={onSaved} />
        </div>
    );
}

const RETENTION_OPTIONS = [
    [0, "Aus (keine automatische Löschung)"],
    [30, "Nach 30 Tagen"],
    [90, "Nach 90 Tagen"],
    [180, "Nach 180 Tagen"],
    [365, "Nach 365 Tagen"],
];

function RetentionSetting({ currentUser, onSaved }) {
    const [days, setDays] = useState(Number(currentUser?.sensitive_upload_retention_days || 0));
    const [busy, setBusy] = useState(false);

    const save = async (val) => {
        setBusy(true);
        try {
            await api.patch("/users/me/dealer-profile", { sensitive_upload_retention_days: val });
            setDays(val);
            toast.success(val > 0 ? `Sensible Uploads werden nach ${val} Tagen automatisch gelöscht.` : "Automatische Löschung deaktiviert.");
            onSaved?.();
        } catch (e) { toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen"); }
        finally { setBusy(false); }
    };

    return (
        <div className="mt-6 pt-6 border-t border-gray-100" data-testid="sensitive-retention-section">
            <div className="font-bold text-[#111827] flex items-center gap-2">
                <Shield className="w-4 h-4 text-[#111827]" /> Aufbewahrung sensibler Kunden-Uploads
            </div>
            <div className="text-xs text-gray-600 mt-1 mb-3">
                Von Kunden hochgeladene Dokumente (z. B. Ausweiskopien zur Leasingrückgabe) sind zugriffsgeschützt — nur Ihr Betrieb (und der Hauptsitz) kann sie öffnen. Optional werden sie nach Ablauf einer Frist automatisch und unwiederbringlich gelöscht (DSGVO-Datenminimierung, Art. 5/17). Beachten Sie ggf. handels-/steuerrechtliche Aufbewahrungspflichten.
            </div>
            <div className="flex items-center gap-2">
                <select
                    value={days}
                    disabled={busy}
                    onChange={(e) => save(Number(e.target.value))}
                    className="rounded-sm border border-gray-300 px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-[#FFCC00] outline-none disabled:opacity-60"
                    data-testid="sensitive-retention-select"
                    aria-label="Aufbewahrungsfrist sensibler Kunden-Uploads"
                >
                    {RETENTION_OPTIONS.map(([v, lbl]) => <option key={v} value={v}>{lbl}</option>)}
                </select>
                {busy && <Loader2 className="w-4 h-4 animate-spin text-gray-500" />}
            </div>
        </div>
    );
}
